import * as React from 'react';

export type DialogPanelSetupOptions<TFooterOptions> = {
  title: string;
  fullSize?: boolean;
  initialFooterOptions?: TFooterOptions;
  onBack?: (() => void) | null;
};

export type DialogPanelSetupContext<TFooterOptions> = {
  setFullSize: (fullSize: boolean) => void;
  setDialogTitle: (title: string) => void;
  setFooterOptions: (options: TFooterOptions) => void;
  setHeaderBackButton?: (onBack: (() => void) | null) => void;
};

/**
 * Hook to standardize panel setup across dialog components.
 * Handles setting the dialog title, fullSize state, footer options, and back button.
 *
 * @param context - The dialog context with setter functions
 * @param options - Configuration for the panel setup
 */
export const useDialogPanelSetup = <TFooterOptions>(
  context: DialogPanelSetupContext<TFooterOptions>,
  options: DialogPanelSetupOptions<TFooterOptions>
): void => {
  const { setFullSize, setDialogTitle, setFooterOptions, setHeaderBackButton } =
    context;

  const { title, fullSize = true, initialFooterOptions, onBack } = options;

  // Store options in refs to avoid re-running effect when callback changes
  const onBackRef = React.useRef(onBack);
  React.useEffect(() => {
    onBackRef.current = onBack;
  }, [onBack]);

  React.useEffect(() => {
    setFullSize(fullSize);
    setDialogTitle(title);
    setFooterOptions((initialFooterOptions ?? {}) as TFooterOptions);

    if (setHeaderBackButton && onBackRef.current !== undefined) {
      // Must wrap in arrow function to prevent React from treating it as an updater function
      // If you pass a function directly to setState, React will CALL it with prev state
      setHeaderBackButton(() => onBackRef.current);
    }

    return () => {
      setFullSize(false);
      if (setHeaderBackButton) {
        setHeaderBackButton(null);
      }
    };
  }, [
    fullSize,
    title,
    initialFooterOptions,
    setFullSize,
    setDialogTitle,
    setFooterOptions,
    setHeaderBackButton,
  ]);
};
